/**
 * «Прогон мониторинга» — кнопка в шапке между ходом прогона и «Запустить генерацию»
 * (docs/release-2026-09-19.md §3.6). Открывает таблицу последнего обхода: что мониторинг
 * прочитал, как оценил и по каким статьям пост уже есть или пишется.
 *
 * ПОЧЕМУ ОНА ЗДЕСЬ, А НЕ НА ВКЛАДКЕ «МОНИТОРИНГ». Таблица — это не отчёт о здоровье
 * мониторинга, а место, откуда ЗАКАЗЫВАЮТ пост: строка за строкой или пачкой. Это действие
 * того же регистра, что «Запустить генерацию», и стоит рядом с ним — по ту же сторону
 * разделителя шапки («что я делаю»), слева от главной кнопки.
 *
 * ТИШЕ ГЛАВНОЙ, А НЕ РЯДОМ С НЕЙ НА РАВНЫХ. Белая, обводка и текст `--violet`, размер
 * `btn-sm`: одно фиолетовое пятно-заливка в шапке остаётся одно (`docs/ui-design.md` §7 —
 * одно primary на смысловой блок), и две кнопки не читаются одним органом.
 *
 * ЛЕСТНИЦА ШАПКИ. На ≤1060 подпись гаснет стилем, а не удаляется: имя органа держат
 * `aria-label` и `title`, значок остаётся (`docs/frontend.md`, «ни один орган не исчезает»).
 * Шаг тот же, на котором «Запустить генерацию» переходит на короткую подпись.
 *
 * ТОЧКА «ИДЁТ СКАН». Скан заказывают из диалога, а длится он минуты — диалог к тому времени
 * давно закрыт. Дышащая точка на значке отвечает на «обход ещё идёт?» тем же взглядом, каким
 * человек ищет кнопку. Опрос дешёвый: один обход (`limit=1`) раз в 15 с, раз в 5 с — пока
 * скан идёт; пока открыт диалог, опрашивает он, а кнопка молчит (`monitoringRuns.ts`).
 *
 * КРАСНАЯ ТОЧКА «ПОСЛЕДНИЙ СКАН НЕ УДАЛСЯ» (ревью партии, U-B4). Отказ скана, случившийся при
 * закрытом диалоге, раньше не сообщался никак: дышащая точка гасла, и это читалось как «обход
 * прошёл». Теперь кнопка знает, какой исход человек уже видел (sessionStorage по бренду —
 * `observeScan` в `monitoringRuns.ts`), и держит красную точку, пока он не откроет диалог:
 * диалог скажет причину словами и отметит отказ просмотренным. Отметка читается при каждой
 * отрисовке, а не хранится в состоянии кнопки: её сдвигает и диалог, открытый со вкладки
 * «Мониторинг», о котором кнопка не знает.
 *
 * БРЕНД БЕЗ МОНИТОРИНГА (docs/multi-brand.md §8). Кнопка стоит на своём месте и своей ширины,
 * но погашена (`aria-disabled`, причина — в имени и подсказке), диалог не открывает и обходы
 * не опрашивает: сканов у такого бренда нет, а старый отказ не должен зажечь точку на
 * кнопке, которую нельзя нажать.
 */

import { type JSX, useCallback, useEffect, useId, useState } from 'react';
import type { GenerationRequest } from '../api/types.ts';
import { offLabel } from '../brand.tsx';
import { IconRadar } from './icons.tsx';
import styles from './MonitoringRunButton.module.css';
import { MonitoringRunDialog } from './MonitoringRunDialog.tsx';
import {
  IDLE_POLL_MS,
  observeScan,
  readScanAck,
  unseenFailure,
  useMonitoringRuns,
} from './monitoringRuns.ts';

const NAME = 'Прогон мониторинга';
const FAILED = 'Последний скан не удался';

export interface MonitoringRunButtonProps {
  readonly brand: string;
  /**
   * Заявка на генерацию, которую только что поставил заказ из таблицы. Едет в полосу хода
   * прогона через шапку — так же, как заявка из «Запустить генерацию»: без этой передачи
   * полоса узнала бы о заказе только следующим фоновым опросом, через 15 с тишины.
   */
  readonly onPicked?: ((request: GenerationRequest) => void) | undefined;
  /**
   * Почему прогон недоступен (`agentsOffReason`, «мониторинг для … ещё не подключён»);
   * `null` или не задано — доступен.
   */
  readonly off?: string | null | undefined;
}

export function MonitoringRunButton({
  brand,
  onPicked,
  off = null,
}: MonitoringRunButtonProps): JSX.Element {
  const stateId = useId();
  const [open, setOpen] = useState(false);
  const runs = useMonitoringRuns(brand, {
    limit: 1,
    enabled: !open && off === null,
    idleMs: IDLE_POLL_MS,
  });
  /**
   * Закрытие — ОДНА функция на всю жизнь кнопки. `Modal` держит фокус эффектом, зависящим от
   * `onClose`: новая функция на каждой отрисовке перезапускала бы его, и фокус прыгал бы из
   * строки таблицы на карточку диалога. А перерисовки здесь штатные: заказ поста отдаёт
   * заявку в шапку (`onPicked`), шапка перерисовывается — и кнопка вместе с ней.
   */
  const close = useCallback(() => {
    setOpen(false);
  }, []);

  // Каждый ответ опроса: вкладка знакомится со сканами бренда (один раз, «первое знакомство —
  // не новость»). Отметку просмотренного двигает только открытый диалог (`viewing`).
  const polled = runs.data;
  const scan = runs.scan;
  useEffect(() => {
    if (polled === undefined) return;
    observeScan(brand, scan, false);
  }, [brand, polled, scan]);

  // Пока диалог открыт, отказ показан в нём словами — точка поверх затемнения лишняя.
  const failed = !open && unseenFailure(scan, readScanAck(brand));
  const state = runs.scanning ? 'идёт мониторинг' : failed ? FAILED.toLowerCase() : null;

  if (off !== null) {
    // Та же разметка и те же классы геометрии — меняются только цвета (`.off`): кнопка
    // обязана занять в шапке ровно прежнее место на каждой ступени лестницы.
    return (
      <button
        type="button"
        className={`btn btn-sm ${styles['button']} ${styles['off']}`}
        aria-label={offLabel(NAME, off)}
        aria-disabled="true"
        title={offLabel(NAME, off)}
      >
        <span className={styles['icon']}>
          <IconRadar />
        </span>
        <span className={styles['label']}>{NAME}</span>
      </button>
    );
  }

  return (
    <>
      <button
        type="button"
        className={`btn btn-sm ${styles['button']}`}
        aria-label={NAME}
        aria-describedby={state === null ? undefined : stateId}
        title={state === null ? NAME : `${NAME} — ${state}`}
        onClick={() => {
          setOpen(true);
        }}
      >
        <span className={styles['icon']}>
          <IconRadar />
          {runs.scanning ? (
            <span className={styles['dot']} aria-hidden="true" data-testid="monitoring-scan-dot" />
          ) : null}
          {failed ? (
            <span
              className={styles['fail']}
              aria-hidden="true"
              title={FAILED}
              data-testid="monitoring-scan-failed"
            />
          ) : null}
        </span>
        <span className={styles['label']}>{NAME}</span>
        {state === null ? null : (
          <span id={stateId} className={styles['srOnly']}>
            {state}
          </span>
        )}
      </button>
      {open ? <MonitoringRunDialog brand={brand} onPicked={onPicked} onClose={close} /> : null}
    </>
  );
}
